import { IconButton, Paper, Typography } from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import CloseIcon from '@material-ui/icons/Close';
import { IQueueItem, QueueItemStatus } from '@wk/elm-uui-context-handler';
import React, { useEffect, useRef } from 'react';
import { useReduxDispatch } from '../../hooks/useReduxDispatch';
import { useReduxSelector } from '../../hooks/useReduxSelector';
import {
    closeFailedUploadFlyout,
    updateBatchUploadFlyoutCount,
    updateUploadingFlyoutState,
} from '../../utils/activitiesService';
import WkErrorIcon from '../icons/wkErrorIcon';
import css from './progressIndicatorFlyout.module.scss';

interface IPopper {
    anchorEl: any;
}

const ProgressIndicatorFlyout: React.FC<IPopper> = ({ anchorEl }) => {
    const [arrowRef, setArrowRef] = React.useState(null);
    const appResources = useReduxSelector((state) => state.appResources);
    const uploadHistoryItems = useReduxSelector((state) => state.ui.uploadHistoryItems);
    const isFailedUploadFlyoutOpen = useReduxSelector((state) => state.ui.historyScreen.isFailedUploadFlyoutOpen);
    const isUploadingFlyoutOpen = useReduxSelector((state) => state.ui.historyScreen.isUploadingFlyoutOpen);
    const reduxDispatch = useReduxDispatch();

    const failedItemsCount = uploadHistoryItems.filter(
        (item: IQueueItem) => item.status === QueueItemStatus.FAILED,
    ).length;
    const itemsUploadingCount = useReduxSelector((state) => state.ui.historyScreen.batchUploadFlyoutCount);

    const isNewUploadCountUpdated = useReduxSelector((state) => state.ui.historyScreen.isNewUploadCountUpdated);

    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (itemsUploadingCount === '' || !isNewUploadCountUpdated) {
            return;
        }
        reduxDispatch({
            type: 'SetNewUploadCountUpdated',
            isUpdated: false,
        });
        if (timer.current) {
            clearTimeout(timer.current);
        }

        if (parseInt(itemsUploadingCount) > 0) {
            updateUploadingFlyoutState(true, reduxDispatch);
            timer.current = setTimeout(() => {
                updateUploadingFlyoutState(false, reduxDispatch);
                updateBatchUploadFlyoutCount('', reduxDispatch);
            }, 5000);
        }
    }, [itemsUploadingCount, isNewUploadCountUpdated, timer, reduxDispatch]);

    const handleArrowRef = (node: any) => {
        setArrowRef(node);
    };

    const handleClose = () => {
        closeFailedUploadFlyout(reduxDispatch);
    };

    return (
        anchorEl && (
            <Popper
                placement="bottom-end"
                open={isFailedUploadFlyoutOpen || isUploadingFlyoutOpen}
                anchorEl={anchorEl}
                className={css.popper}
                modifiers={{
                    flip: {
                        enabled: true,
                    },
                    preventOverflow: {
                        enabled: 'true',
                        boundariesElement: 'scrollParent',
                    },
                    arrow: {
                        enabled: true,
                        element: arrowRef,
                    },
                }}
                data-testid="message-popper">
                <span className={css.arrow} ref={handleArrowRef} />
                <Paper className={css.paper}>
                    {isFailedUploadFlyoutOpen && failedItemsCount > 0 && (
                        <div
                            className={
                                isFailedUploadFlyoutOpen && isUploadingFlyoutOpen
                                    ? css.errorMessageContainer
                                    : undefined
                            }
                            data-testid="error-message-popper">
                            <div className={css.errorMessage}>
                                <WkErrorIcon fontSize="small" className={css.errorIcon} />
                                <Typography className={css.message} data-testid="failed-upload-popper">
                                    {failedItemsCount > 1
                                        ? appResources.historyItemFailedMessage.replace(
                                              '{items}',
                                              failedItemsCount.toString(),
                                          )
                                        : appResources.historyItemFailedMessage
                                              .replace('{items}', failedItemsCount.toString())
                                              .replace('items', 'item')}
                                </Typography>
                                <IconButton
                                    size="small"
                                    className={css.closeIconButton}
                                    onClick={() => handleClose()}
                                    data-testid="flyout-close-button"
                                    aria-label="Close Flyout">
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </div>
                        </div>
                    )}
                    {isUploadingFlyoutOpen ? (
                        <Typography
                            className={
                                isFailedUploadFlyoutOpen && isUploadingFlyoutOpen
                                    ? css.uploadMessageWithError
                                    : css.uploadingMessage
                            }
                            data-testid="upload-message-popper">
                            {parseInt(itemsUploadingCount) > 1
                                ? appResources.historyItemProgressIndicatorUploading.replace(
                                      '{0}',
                                      itemsUploadingCount.toString(),
                                  )
                                : appResources.historyItemProgressIndicatorUploading
                                      .replace('{0}', itemsUploadingCount.toString())
                                      .replace('items', 'item')}
                        </Typography>
                    ) : null}
                </Paper>
            </Popper>
        )
    );
};

export default ProgressIndicatorFlyout;
